import { useTranslator } from '@bluebeam/react-i18n';
import FilterSection from 'components/FilterPopover/FilterSection';
import Checkbox from 'components/inputs/Checkbox';
import SearchInput from 'components/inputs/SearchInput';
import AppStoreContext from 'contexts/AppStoreContext';
import Fuse from 'fuse.js';
import React, { useContext } from 'react';
import { SheetQuery } from 'records/SheetQuery';
import { SheetVersion } from 'records/SheetVersion';
import { OrderedMap } from 'immutable';
import { Discipline, DisciplineDesignator } from 'records/Discipline';

interface DisciplineSearch {
  key: string;
  discipline: string;
  size: number;
  description: string;
  item: Discipline;
  sortOrder: number;
}

const searchOptions: Fuse.IFuseOptions<DisciplineSearch> = {
  shouldSort: false,
  threshold: 0.2,
  keys: ['description'],
};

interface Props {
  sheetQuery: SheetQuery;
  updateSheetQuery: (nextSheetQuery: SheetQuery) => void;
}

const DisciplineFilterSection: React.FC<Props> = (props: Props) => {
  const { translate } = useTranslator(['disciplineFilterSection']);
  const { sheetQuery, updateSheetQuery } = props;
  const { state: appState } = useContext(AppStoreContext);

  const disciplines = appState.sheets.reduce((acc, sheet) => {
    const sheetVersion = sheet.versions.last(new SheetVersion());

    const sheetDiscipline = sheetVersion.get('sheetDiscipline');
    const count = acc.has(sheetDiscipline.designator)
      ? acc.getIn([sheetDiscipline.designator, 'count'])
      : 0;

    return acc.set(
      sheetDiscipline.designator,
      sheetDiscipline.set('count', count + 1)
    );
  }, OrderedMap<DisciplineDesignator, Discipline>());

  const searchObjs = disciplines
    .map<DisciplineSearch>((v) => {
      return {
        key: v.get('designator'),
        discipline: v.get('description'),
        description: v.get('description'),
        sortOrder: v.get('sortOrder'),
        size: v.get('count'),
        item: v,
      };
    })
    .sortBy((o) => o.sortOrder)
    .valueSeq()
    .toArray();

  const fuse = new Fuse(searchObjs, searchOptions);

  const searchedDisciplines =
    sheetQuery.disciplineSearch.length > 0
      ? fuse.search(sheetQuery.disciplineSearch).map((ds) => ds.item)
      : searchObjs;
  /* */

  /* Handlers */
  function onSearchChange(nextSearch: string) {
    const nextSheetQuery = sheetQuery.set('disciplineSearch', nextSearch);
    updateSheetQuery(nextSheetQuery);
  }

  function onCheckboxChange(discipline: Discipline) {
    const nextSheetQuery = sheetQuery
      .update('disciplines', (d) => {
        return d.has(discipline.designator)
          ? d.delete(discipline.designator)
          : d.add(discipline.designator);
      })
      .update('sheetDisciplines', (d) => {
        return d.has(discipline.designator)
          ? d.delete(discipline.designator)
          : d.set(discipline.designator, discipline);
      });
    updateSheetQuery(nextSheetQuery);
  }

  /* */

  const appliedDisciplinesFilters = sheetQuery.sheetDisciplines
    .map((discipline) => discipline.description)
    .sort()
    .valueSeq()
    .toArray();

  return (
    <FilterSection
      label={translate('disciplineFilterSection:discipline')}
      appliedFilters={appliedDisciplinesFilters}
      disabled={disciplines.size === 0}
    >
      <div className="h100 column pos-r">
        <div className="px4 py3 pos-a hrem-4 bgd-white w100">
          <SearchInput
            id="drawings-filter-popover-discipline-search"
            value={sheetQuery.disciplineSearch}
            onChange={onSearchChange}
          />
        </div>
        <div className="mt8 px4 oa">
          {searchedDisciplines.map(
            ({ key, size, description, item }: DisciplineSearch) => {
              return (
                <div
                  key={key}
                  className="row mb3 cursor-pointer"
                  onClick={() => onCheckboxChange(item)}
                >
                  <Checkbox
                    id={`drawings-filter-popover-discipline-checkbox-${key}`}
                    onChange={() => {
                      onCheckboxChange(item);
                    }}
                    checked={sheetQuery.disciplines.has(key)}
                  />
                  <div className="ml2 disable-select">
                    {translate(
                      `disciplineFilterSection:disciplines.${description}`,
                      {
                        defaultValue: description,
                      }
                    )}{' '}
                    ({size})
                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>
    </FilterSection>
  );
};

export default DisciplineFilterSection;
